import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { tokenStorage } from '@api/lib/token-storage';
import { useAuthStore } from '@stores/auth-store';

import { authApi } from '../services/auth.api';
import type { SendOtpData, VerifyOtpData } from '../types';

// ─── Shared Session Handler ───────────────────────────────────────────────

/**
 * COMMON FRONTEND METHOD: Reused by both OTP verify and Quick Login
 */
export function handleLoginSuccess(
  data: VerifyOtpData,
  refreshToken?: string | null,
  _redirectTo = '/dashboard',
) {
  // 1. Save tokens
  if (refreshToken) {
    tokenStorage.setRefreshToken(refreshToken);
  }
  tokenStorage.setAccessToken(data.userDetails.accessToken);

  const u = data.userDetails;
  const role = u.member_type ?? 'Owner';
  const permissions = u.permissions ?? (role === 'Owner' ? ['*'] : []);
  tokenStorage.setPermissions(permissions);

  // 2. Set Auth Store
  useAuthStore.getState().setCredentials(
    {
      id: String(u.business_id),
      name: `${u.name}${u.surname ? ` ${u.surname}` : ''}`.trim(),
      email: undefined,
      mobile: u.mobile_no,
      role,
      permissions,
      avatarUrl: u.profile_image ?? undefined,
      code: u.system_code,
      businessName: u.business_name,
      business_type_id: u.business_type_id,
    },
    u.accessToken,
  );

  // 3. Set Workshop Context
  useAuthStore.getState().setWorkshop({
    id: String(u.business_id),
    name: u.business_name,
    code: u.system_code,
    branchId: '',
    branchName: u.business_name,
    financialYearId: '',
    financialYearLabel: '',
    currency: 'INR',
    locale: 'en-IN',
    timezone: 'Asia/Kolkata',
  });
}

// ─── useSendOtp ───────────────────────────────────────────────────────────────

/**
 * Mutation to send OTP to a mobile number.
 * Returns SendOtpData { verification_id, expired_in } on success.
 */
export function useSendOtp() {
  return useMutation<SendOtpData, Error, number>({
    mutationFn: (mobileNumber) => authApi.sendOtp(mobileNumber),
  });
}

// ─── useVerifyOtp ─────────────────────────────────────────────────────────────

interface VerifyOtpInput {
  verificationId: number;
  otp: number;
  redirectTo?: string;
}

/**
 * Mutation to verify OTP.
 * On success: stores tokens, maps response to AuthUser + WorkshopContext,
 * updates auth store, and navigates to the intended path.
 */
export function useVerifyOtp() {
  const navigate = useNavigate();

  return useMutation<{ data: VerifyOtpData; refreshToken: string | null }, Error, VerifyOtpInput>({
    mutationFn: ({ verificationId, otp }) => authApi.verifyOtp(verificationId, otp),
    onSuccess: ({ data, refreshToken }, { redirectTo = '/dashboard' }) => {
      handleLoginSuccess(data, refreshToken, redirectTo);
      navigate(redirectTo, { replace: true });
    },
  });
}

// ─── useQuickLogin ────────────────────────────────────────────────────────────

/**
 * Mutation for Quick Login via one-time 32-char token.
 */
export function useQuickLogin() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation<{ data: VerifyOtpData; refreshToken: string | null }, Error, string>({
    mutationFn: (token: string) => authApi.quickLogin(token),
    onSuccess: async ({ data, refreshToken }) => {
      // 1. Wipe out any old garage cache completely
      queryClient.clear();
      // 2. Set credentials, permissions, and workshop context into store
      handleLoginSuccess(data, refreshToken, '/dashboard');
      // 3. Reset and invalidate all queries so the dashboard refetches immediately
      await queryClient.resetQueries();
      navigate('/dashboard', { replace: true });
    },
  });
}

// ─── useResendOtp ─────────────────────────────────────────────────────────────

/**
 * Mutation to resend OTP.
 * Returns new SendOtpData { verification_id, expired_in }.
 */
export function useResendOtp() {
  return useMutation<SendOtpData, Error, number>({
    mutationFn: (verificationId) => authApi.resendOtp(verificationId),
  });
}

// ─── useLogout ────────────────────────────────────────────────────────────────

/**
 * Mutation to logout the current device session.
 * Clears tokens and auth state regardless of API response.
 */
export function useLogout() {
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  return useMutation<void, Error, void>({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      tokenStorage.removeRefreshToken();
      tokenStorage.removeAccessToken();
      tokenStorage.removePermissions();
      logout();
      navigate('/login', { replace: true });
    },
  });
}
